import Icon from '../Utilites/Icon';
import React, { useEffect, useRef } from 'react';
//import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import { AnimatedRouteScore } from '../Utilites/AnimatedRoutes';
import { storageUrl } from '../Core/Config';
import { submitEvaluationResult } from '../Core/Evaluation';

const QuizComplete = ({ score, questions, resetQuizState, quiz, lang, group, area, auth }) => {
 //   const navigate = useNavigate();
    const { t } = useTranslation();
    const evaluationSubmittedRef = useRef(false);

    const handleRestartQuiz = () => {
        resetQuizState(); 
    };

    const convertBooleanToText = (answer) => {
        return answer === true ? t('yes') : t('no');
    };

    const renderTextCompletionResult = (question) => {
        const gaps = Object.keys(question.correctAnswers || {});
        const userAnswers = (question.userAnswer || '').split(',').map(w => w.trim());
 
        let text = question.text || '';

        gaps.forEach((gapId, index) => {
            const correctWord = question.correctAnswers[gapId];
            const userWord = userAnswers[index] || '';
            const isCorrect = userWord.toLowerCase() === String(correctWord).toLowerCase();

            const className = isCorrect ? 'text-green' : 'text-red';
            const content = `<strong class="${className}">${userWord || '—'}</strong>`;

            text = text.replace(`{{${gapId}}}`, content);
        });

        return { __html: text };
    };

    const calculateResultScore = () => {
        if (!questions || questions.length === 0) {
            return { score: 0, maxScore: 0 };
        }

        if (quiz?.type === 'textCompletion') {
            const correctAnswers = questions[0]?.correctAnswers || {};
            const userAnswers = (questions[0]?.userAnswer || '').split(',').map(w => w.trim().toLowerCase());
            let correctCount = 0;

            Object.entries(correctAnswers).forEach(([_, correctWord], index) => {
                if ((userAnswers[index] || '') === String(correctWord).toLowerCase()) {
                    correctCount++;
                }
            });

            return { score: correctCount, maxScore: Object.keys(correctAnswers).length };
        }

        if (quiz?.type === 'audioSelect') {
            let audioScore = 0;
            let audioMax = 0;

            questions.forEach(q => {
                if (typeof q.status === 'string' && q.status.includes('/')) {
                    const [correct, total] = q.status.split('/').map(Number);
                    audioScore += Number.isFinite(correct) ? correct : 0;
                    audioMax += Number.isFinite(total) ? total : 0;
                }
            });

            return { score: Math.max(0, audioScore), maxScore: audioMax };
        }

        return { score: Math.max(0, score), maxScore: questions.length };
    };

    const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    const normalizeAnswerValue = (value) => {
        if (value === null || value === undefined || typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') {
            return value ?? null;
        }

        if (Array.isArray(value)) {
            return value.map(normalizeAnswerValue);
        }

        if (typeof value === 'object') {
            return Object.fromEntries(
                Object.entries(value)
                    .filter(([key]) => !['image1', 'image2', 'image3', 'options'].includes(key))
                    .map(([key, item]) => [key, normalizeAnswerValue(item)])
            );
        }

        return String(value);
    };

    const createAnswerPayload = () => (questions || []).map((question, index) => ({
        index: index + 1,
        id: question.id || null,
        status: question.status || null,
        question: stripHtml(question.question || question.questionText || question.text || ''),
        correctAnswer: normalizeAnswerValue(question.correctAnswer ?? question.correctAnswers ?? question.correctWords ?? null),
        userAnswer: normalizeAnswerValue(question.userAnswer ?? null),
        optionsWords: normalizeAnswerValue(question.optionsWords ?? null),
    }));

    useEffect(() => {
        if (!questions || questions.length === 0) return;

        let isPerfectScore = false;
        if (quiz?.type === 'textCompletion') {
            const correctAnswers = questions[0]?.correctAnswers;
            const userAnswers = (questions[0]?.userAnswer || '').split(',').map(w => w.trim().toLowerCase());
            let correctCount = 0;
            Object.entries(correctAnswers).forEach(([_, correctWord], index) => {
                if ((userAnswers[index] || '') === correctWord.toLowerCase()) correctCount++;
            });
            isPerfectScore = correctCount === Object.keys(correctAnswers).length;
        } else if (quiz?.type === 'audioSelect') {
            let audioSelectScore = 0;
            let audioSelectMax = 0;
            questions.forEach(q => {
                if (typeof q.status === 'string' && q.status.includes('/')) {
                    const [correct, total] = q.status.split('/').map(Number);
                    audioSelectScore += correct;
                    audioSelectMax += total;
                }
            });
            isPerfectScore = audioSelectScore === audioSelectMax && audioSelectMax > 0;
        } else {
            isPerfectScore = score === questions.length;
        }

        const audioPath = isPerfectScore ? '/sounds/quiz-success.mp3' : '/sounds/quiz-fail.mp3';
        const audio = new Audio(storageUrl + audioPath);
        audio.play();
    }, [questions, quiz?.type, score]);

    useEffect(() => {
        if (evaluationSubmittedRef.current || !auth?.authenticated || !auth?.authorized || !area?.id || !quiz?.id) {
            return;
        }

        const result = calculateResultScore();

        if (result.maxScore <= 0) {
            return;
        }

        evaluationSubmittedRef.current = true;

        submitEvaluationResult({
            language: {
                code: lang?.code || '',
                name: lang?.name || '',
            },
            group: {
                id: group?.id || null,
                name: group?.name || null,
            },
            lesson: {
                id: area.id,
                name: area.name || '',
            },
            quiz: {
                id: quiz.id,
                name: quiz.name || '',
                type: quiz.type || '',
            },
            score: result.score,
            maxScore: result.maxScore,
            completedAt: new Date().toISOString(),
            answers: createAnswerPayload(),
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth, area?.id, quiz?.id]);

    let audioSelectScore = 0;
    let audioSelectMax = 0;
    if (quiz?.type === 'audioSelect' && questions?.length > 0) {
        questions.forEach(q => {
            if (typeof q.status === 'string' && q.status.includes('/')) {
                const [correct, total] = q.status.split('/').map(Number);
                audioSelectScore += correct;
                audioSelectMax += total;
            }
        });
    }

    return (
        <AnimatePresence>
            <AnimatedRouteScore>
                <div id="quiz-complete">
                    <div className="flex flex-col items-center justify-center gap-4">
                        <h1 className="text-primary">{t('quiz-complete')}</h1>

                        {quiz?.type === 'textCompletion' && questions?.[0] ? (() => {
                            const correctAnswers = questions[0].correctAnswers;
                            const userAnswers = (questions[0].userAnswer || '').split(',').map(w => w.trim().toLowerCase());
                            let correctCount = 0;
                            Object.entries(correctAnswers).forEach(([_, correctWord], index) => {
                                const userWord = userAnswers[index] || '';
                                if (userWord === correctWord.toLowerCase()) correctCount++;
                            });
                            return (
                                <>
                                    <h3>{t('correct-answers')}: <strong>{correctCount} {t('of')} {Object.keys(correctAnswers).length}</strong></h3>
                                    <div
                                        className="textCompletionResultP text-lg text-center mt-4 leading-loose"
                                        dangerouslySetInnerHTML={renderTextCompletionResult(questions[0])}
                                    />
                                </>
                            );
                        })() : quiz?.type === 'audioSelect' ? (
                            <h3>{t('correct-answers')}: <strong>{audioSelectScore} {t('of')} {audioSelectMax}</strong></h3>
                        ) : (
                            <h3>{t('correct-answers')}: <strong>{score} {t('of')} {questions?.length}</strong></h3>
                        )}

                        {quiz?.type === 'listen' && questions && questions.length > 0 && (
                            <div id="quiz-results">
                                <h3 className="text-primary text-center !mt-8">{t('your-answers')}:</h3>
                                <div className="flex flex-col gap-2">
                                    {questions.map((question, index) => (
                                        <div
                                            className={`quiz-question-view-mini relative flex flex-row items-center gap-2 rounded-[10px] border-2 border-solid pr-3 ${question.status === 'correct' ? 'border-green bg-green-100' : 'border-red bg-red-100'}`}
                                            key={`queston-answer-${question.id}-${index}`}
                                        >
                                            {question.options && typeof question.correctIndex === 'number' && (
                                                <div className="flex-shrink-0">
                                                    <img src={question.options[question.correctIndex]} alt="question" className="w-20 h-20 object-contain bg-white border border-gray-300 rounded" />
                                                </div>
                                            )}
                                            <div className="flex-grow text-secondary">
                                                <p className="flex flex-row justify-between items-center gap-4">
                                                    <strong>{question.questionText || ''}</strong>
                                                </p>
                                                <p className="flex flex-row justify-between items-center gap-4">
                                                    <span>{t('correct-answer')}:</span>
                                                    <strong className="text-green-700">{question.optionsWords && typeof question.correctIndex === 'number' ? (question.optionsWords[question.correctIndex] ?? '') : ''}</strong>
                                                </p>
                                                <p className="flex flex-row justify-between items-center gap-4 font-italic">
                                                    {t('your-answer')}: <strong className={question.status === 'correct' ? 'text-green-700' : 'text-red-700'}>{question.optionsWords && typeof question.userAnswer === 'number' ? (question.optionsWords[question.userAnswer] ?? '') : ''}</strong>
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {quiz?.type === 'listen3' && questions && questions.length > 0 && (
                            <div id="quiz-results">
                                <h3 className="text-primary text-center !mt-8">{t('your-answers')}:</h3>
                                <div className="flex flex-col gap-4">
                                    {questions.map((question, index) => (
                                        <div
                                            className={`quiz-question-view-mini relative flex flex-row items-center gap-4 rounded-[10px] border-2 border-solid pr-3 ${question.status === 'correct' ? 'border-green bg-green-100' : 'border-red bg-red-100'}`}
                                            key={`queston-answer-${question.id}-${index}`}
                                        >
                                            {/* Vlevo obrázky správné odpovědi */}
                                            <div className="flex flex-col items-center">
                                                <div className="flex gap-1">
                                                    {question.options && typeof question.correctIndex === 'number' && question.options[question.correctIndex].map((img, imgIdx) => (
                                                        <img key={imgIdx} src={img} alt={`Správný obrázek ${imgIdx + 1}`} className="w-16 h-16 object-cover" />
                                                    ))}
                                                </div>
                                            </div>
                                            {/* Vpravo textově správná a uživatelova odpověď */}
                                            <div className="flex flex-col items-start justify-center flex-grow gap-2 text-secondary">
                                                <div>
                                                    <strong className="mr-2">{t('correct-answer')}:</strong>
                                                    <span className="">{question.correctWords ? question.correctWords.join(', ') : ''}</span>
                                                </div>
                                                <div>
                                                    <strong className="mr-2">{t('your-answer')}:</strong>
                                                    <span className="">
                                                        {question.optionsWords && typeof question.userAnswer === 'number' ? (Array.isArray(question.optionsWords[question.userAnswer]) ? question.optionsWords[question.userAnswer].join(', ') : question.optionsWords[question.userAnswer]) : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {/* Ostatní typy */}
                        {quiz?.type === 'audioSelect' && questions && questions.length > 0 && (
                            <div id="quiz-results">
                                <h3 className="text-primary text-center !mt-8">{t('your-answers')}:</h3>
                                <div className="flex flex-col gap-2">
                                    {questions.map((question, index) => (
                                        <div
                                            className={`quiz-question-view-mini relative flex flex-row items-center gap-2 rounded-[10px] border-2 border-solid border-primary bg-primary-100 pr-3 ${question.status === 'correct' ? 'border-green bg-green-100' : question.status === 'incorrect' ? 'border-red bg-red-100' : 'border-gray-300 bg-white'}`}
                                            key={`queston-answer-${question.id}-${index}`}
                                        >
                                            <div className="flex flex-wrap gap-2"></div>
                                            <div className="flex-grow text-secondary">
                                                <p>
                                                    <strong>{t('correct-answers')}:</strong>
                                                    {question.correctAnswers.map((ans, i) => (
                                                        <span key={i} className="ml-1 text-gray-800 font-bold">
                                                            {ans}{i < question.correctAnswers.length - 1 ? ', ' : ''}
                                                        </span>
                                                    ))}
                                                </p>
                                                <p>
                                                    <strong>{t('your-answer')}:</strong>
                                                    {Array.isArray(question.userAnswer) && question.userAnswer.length > 0 ? (
                                                        question.userAnswer.map((ans, i) => {
                                                            const isCorrect = question.correctAnswers.includes(ans);
                                                            return (
                                                                <span
                                                                    key={i}
                                                                    className={isCorrect ? 'ml-1 text-green-700 font-bold' : 'ml-1 text-red-700 font-bold'}
                                                                >
                                                                    {ans}{i < question.userAnswer.length - 1 ? ', ' : ''}
                                                                </span>
                                                            );
                                                        })
                                                    ) : (
                                                        <span className="ml-1 text-gray-500">-</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {quiz?.type !== 'textCompletion' && quiz?.type !== 'listen' && quiz?.type !== 'listen3' && quiz?.type !== 'audioSelect' && questions && questions.length > 0 && (
                            <div id="quiz-results">
                                <h3 className="hidden text-primary text-center !mt-8">{t('your-answers')}:</h3>
                                <div className="flex flex-col gap-2">
                                    {questions.map((question, index) => (
                                        <div
                                            className={`quiz-question-view-mini relative flex flex-row items-center gap-2 rounded-[10px] border-2 border-solid pr-3 ${question.status === 'correct' ? ' border-green bg-green-100' : 'border-red bg-red-100'}`}
                                            key={`queston-answer-${question.id}-${index}`}
                                        >
                                            {(question.image1 || question.image2 || question.image3) && (
                                                <div className={`images ${question.image2 || question.image3 ? 'multiple' : 'single'}`}>
                                                    {question.image1 && <div><img src={question.image1} alt="" /></div>}
                                                    {question.image2 && <div><img src={question.image2} alt="" /></div>}
                                                    {question.image3 && <div><img src={question.image3} alt="" /></div>}
                                                </div>
                                            )}
                                            <div className="flex-grow text-secondary">
                                                <p className="flex flex-row justify-between items-center gap-4">
                                                    <strong dangerouslySetInnerHTML={{ __html: question.question?.replace(/<br\s*\/?>/gi, '') || t('correct-answer') }}></strong>
                                                    <strong>{typeof question.correctAnswer === 'boolean' ? convertBooleanToText(question.correctAnswer) : question.correctAnswer}</strong>
                                                </p>
                                                <p className="flex flex-row justify-between items-center gap-4 font-italic">
                                                    {t('your-answer')}: <strong className={question.status === 'correct' ? 'text-green' : 'text-red'}>{typeof question.userAnswer === 'boolean' ? convertBooleanToText(question.userAnswer) : question.userAnswer}</strong>
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex flex-row justify-center items-center gap-4 mt-8">
                            <button onClick={handleRestartQuiz} className="btn-primary">
                                <Icon name="refresh" /> {t('restart-quiz')}
                            </button>
                        </div>
                    </div>
                </div>
            </AnimatedRouteScore>
        </AnimatePresence>
    );
};

export default QuizComplete;
